import { create } from "zustand";
import type { Tab } from "@playbookai/ui";

/**
 * Tab slice — owns the live list of open tabs and the active id.
 *
 * `AppLayout` checkpoints the serialisable parts of this state to scoped
 * sessionStorage so a hard reload can restore the strip. Icons and module
 * metadata are reconstructed from the current registry; this store remains
 * storage-agnostic and is still the only live source of truth. See ADR-0009.
 *
 * No "Home" tab — the top-bar home icon is the only entry point for the Home
 * page. Empty `tabs` is a valid state and renders just the `+` button.
 *
 * The `Tab` shape is re-exported from `@playbookai/ui` so the store and the
 * library agree without one importing the other.
 */
export type { Tab } from "@playbookai/ui";

export interface TabsState {
  readonly tabs: readonly Tab[];
  readonly activeTabId: string;
  /**
   * Monotonic per-kind counter for tab instance ids. Keyed by tab "kind" —
   * a module id (`solve`, `vsm`, ...) or the literal `launcher`. Values are
   * the *last* used number, so the next id is `count + 1`. The session
   * checkpoint preserves these values to prevent id reuse after refresh.
   */
  readonly counters: Readonly<Record<string, number>>;
  /**
   * Where each tab was last seen — `pathname + search`, keyed by tab id.
   *
   * `Tab.route` is the route a tab was CREATED with and is its stable IDENTITY:
   * `findTabForPath`, the settings-tab check and `isModuleHomeTab` all compare
   * it, and `isModuleHomeTab` rejects a route with a `/` past the module prefix
   * — so a `route` that tracked a live deep path would stop a home tab reading
   * as home and the launcher would mint a duplicate. This map is the mutable
   * half, kept separate for that reason (PB-5295).
   */
  readonly lastRouteById: Readonly<Record<string, string>>;
  /** Bump and return the next counter value for the given kind. */
  nextCount(kind: string): number;
  /** Record where a tab was last seen. No-ops when the STORED value matches. */
  rememberRoute(id: string, route: string): void;
  /** Open a tab, or refresh + activate it if the same entity id already exists. */
  openTab(tab: Tab): void;
  /** Replace one tab in place (used when the launcher tab "becomes" a module tab). */
  replaceTab(oldId: string, next: Tab): void;
  /** Toggle the `pinned` flag on a tab. */
  togglePin(id: string): void;
  /** Close one tab. Pinned tabs are skipped silently. */
  closeTab(id: string): void;
  /** Close one tab even when pinned (terminal resource revocation only). */
  forceCloseTab(id: string): void;
  /** Close every closable tab except `keepId`. Pinned tabs survive. */
  closeOthers(keepId: string): void;
  setActive(id: string): void;
}

export const useTabs = create<TabsState>((set, get) => ({
  tabs: [],
  activeTabId: "",
  counters: {},
  lastRouteById: {},

  rememberRoute(id, route) {
    // Compare the STORED value, never `tab.route`: comparing against the
    // declared route would let a stale deeper entry survive for ever, because
    // the incoming route would differ from `tab.route` every time and the write
    // would look necessary while never being compared to what is actually held.
    if (get().lastRouteById[id] === route) return;
    set((s) => ({ lastRouteById: { ...s.lastRouteById, [id]: route } }));
  },

  nextCount(kind) {
    const current = get().counters[kind] ?? 0;
    const next = current + 1;
    set((s) => ({ counters: { ...s.counters, [kind]: next } }));
    return next;
  },

  openTab(tab) {
    const exists = get().tabs.some((t) => t.id === tab.id);
    if (exists) {
      set((s) => ({
        tabs: s.tabs.map((current) =>
          current.id === tab.id
            ? tab.pinned !== undefined || current.pinned !== undefined
              ? { ...tab, pinned: tab.pinned ?? current.pinned ?? false }
              : tab
            : current,
        ),
        activeTabId: tab.id,
      }));
      return;
    }
    set((s) => ({ tabs: [...s.tabs, tab], activeTabId: tab.id }));
  },

  replaceTab(oldId, next) {
    set((s) => {
      const idx = s.tabs.findIndex((t) => t.id === oldId);
      const clash = s.tabs.findIndex((t) => t.id === next.id);
      // `next.id` is already open elsewhere: drop the source pill and focus the
      // incumbent instead of inserting a second tab under one id. Without this
      // the strip can hold two tabs with the same id — React warns on the
      // duplicate key, and `closeTab` (which filters by id) then removes BOTH,
      // taking out a tab the user never asked to close.
      //
      // Reachable because the deep-link bootstrap in AppLayout reuses the
      // instance id from the URL WITHOUT bumping the per-module counter, so a
      // later launcher pick can mint an id that is already on screen.
      // `openTab` has always de-duplicated; this closes the same hole here.
      if (clash !== -1 && clash !== idx) {
        return {
          tabs: s.tabs.filter((t) => t.id !== oldId),
          activeTabId: next.id,
          lastRouteById: withoutRoutes(s.lastRouteById, [oldId]),
        };
      }
      if (idx === -1) {
        // Source missing — fall back to opening (matches launcher cold-start).
        return { tabs: [...s.tabs, next], activeTabId: next.id };
      }
      const tabs = s.tabs.map((t, i) => (i === idx ? next : t));
      const activeTabId = s.activeTabId === oldId ? next.id : s.activeTabId;
      // Drop the old memory ONLY when the id genuinely changes. `replaceTab` is
      // also the RENAME path — `setTabLabel` calls it with the same id
      // (`layout/tab-label-sync.ts`) — and an unconditional drop would erase a
      // live tab's route on every title sync. This one rule also covers the
      // clash branch above, whose guard cannot hold when the ids are equal.
      return next.id === oldId
        ? { tabs, activeTabId }
        : {
            tabs,
            activeTabId,
            lastRouteById: withoutRoutes(s.lastRouteById, [oldId]),
          };
    });
  },

  togglePin(id) {
    set((s) => ({
      tabs: s.tabs.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)),
    }));
  },

  closeTab(id) {
    const tab = get().tabs.find((t) => t.id === id);
    if (!tab || tab.pinned) return;
    set((s) => removeTab(s, id));
  },

  forceCloseTab(id) {
    set((s) => removeTab(s, id));
  },

  closeOthers(keepId) {
    set((s) => {
      const tabs = s.tabs.filter((t) => t.id === keepId || t.pinned);
      const stillActive = tabs.some((t) => t.id === s.activeTabId);
      const activeTabId = stillActive ? s.activeTabId : (tabs[0]?.id ?? "");
      // Derived from what was actually REMOVED, not from the operation's name:
      // pinned tabs survive this call, and dropping everything but `keepId`
      // would erase their memories — the PB-5295 bug, on exactly the tabs a
      // user cared enough to pin.
      const kept = new Set(tabs.map((t) => t.id));
      const removed = s.tabs.filter((t) => !kept.has(t.id)).map((t) => t.id);
      return {
        tabs,
        activeTabId,
        lastRouteById: withoutRoutes(s.lastRouteById, removed),
      };
    });
  },

  setActive(id) {
    set({ activeTabId: id });
  },
}));

function removeTab(
  state: Pick<TabsState, "tabs" | "activeTabId" | "lastRouteById">,
  id: string,
): Pick<TabsState, "tabs" | "activeTabId" | "lastRouteById"> {
  const idx = state.tabs.findIndex((tab) => tab.id === id);
  if (idx === -1) return state;
  const tabs = state.tabs.filter((tab) => tab.id !== id);
  const activeTabId =
    state.activeTabId === id
      ? (tabs[idx]?.id ?? tabs[idx - 1]?.id ?? "")
      : state.activeTabId;
  return {
    tabs,
    activeTabId,
    lastRouteById: withoutRoutes(state.lastRouteById, [id]),
  };
}

/** A copy of the map without `ids`, or the same reference when nothing matched. */
function withoutRoutes(
  map: Readonly<Record<string, string>>,
  ids: readonly string[],
): Readonly<Record<string, string>> {
  if (!ids.some((id) => id in map)) return map;
  const next = { ...map };
  for (const id of ids) delete next[id];
  return next;
}

/**
 * Reset every slice of the tab store.
 *
 * Test-only, and exported so the next field added above cannot repeat the leak
 * this closes: `useTabs.setState({ tabs, activeTabId, counters })` is a zustand
 * PARTIAL merge, so five specs were resetting three of four slices and carrying
 * `lastRouteById` into the next case — which would make ordering matter and
 * could green a broken cleanup.
 */
export function resetTabsForTests(): void {
  replaceTabsForSession({
    tabs: [],
    activeTabId: "",
    counters: {},
    lastRouteById: {},
  });
}

/**
 * Replace every persisted slice as one store update during session hydration.
 * Keeping this operation here prevents consumers from accidentally using
 * Zustand's partial merge and carrying a stale counter or remembered route
 * across an authenticated user/tenant boundary.
 */
export function replaceTabsForSession(
  state: Pick<TabsState, "tabs" | "activeTabId" | "counters" | "lastRouteById">,
): void {
  useTabs.setState({
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    counters: state.counters,
    lastRouteById: state.lastRouteById,
  });
}

/**
 * The tab that owns `path`: an exact route match, else the MOST SPECIFIC route
 * that is an ancestor prefix of it.
 *
 * Moved here from `layout/AppLayout.tsx` (PB-5295) because `routeForTab` below
 * needs it and lives in the store. It is pure — `(tabs, path)` in, a tab out —
 * so it belongs to the tab model rather than to the layout.
 *
 * Prefix fallback picks the most specific route, not the first in the strip.
 * Order-dependence became a real defect once a module's home could sit at its
 * bare `routeBase`: such a home is an ancestor prefix of every one of that
 * module's entity routes, so a nested route like
 * `/app/hoshin/<planId>/tti/<itemId>` matched BOTH the plan tab and the home
 * tab — and first-match handed it to whichever pill happened to be opened
 * earlier.
 */
export function findTabForPath(
  tabs: readonly Tab[],
  path: string,
): Tab | undefined {
  for (const t of tabs) {
    if (path === t.route) return t;
  }
  let best: Tab | undefined;
  for (const t of tabs) {
    if (!path.startsWith(`${t.route}/`)) continue;
    if (best === undefined || t.route.length > best.route.length) best = t;
  }
  return best;
}

/**
 * Where activating a tab should navigate: the route the user was last on inside
 * it, falling back to the route it was created with.
 *
 * The fallback is not only for a tab with no memory. A remembered route is
 * honoured ONLY while it still resolves back to the same tab — the SELF-HEAL.
 * Ownership is not stable: a more specific tab created later steals a path that
 * an ancestor legitimately owned when it was recorded. Without this check the
 * ancestor's pill would navigate into the other tab's route and hand focus
 * straight to it, leaving the ancestor unreachable from its own pill.
 *
 * This is NOT redundant with the recording-side guard in `AppLayout`. That guard
 * refuses to record a path whose module declares a more specific entity route;
 * it cannot help where no such route is declared (SOLVE and LAMA declare no
 * `entityRouteDepth`), and the theft happens later. Each covers what the other
 * misses — do not delete either as duplication.
 */
export function routeForTab(id: string): string | undefined {
  const { tabs, lastRouteById } = useTabs.getState();
  const tab = tabs.find((t) => t.id === id);
  if (!tab) return undefined;
  const remembered = lastRouteById[id];
  if (!remembered) return tab.route;
  const path = remembered.split("?")[0] ?? remembered;
  return findTabForPath(tabs, path)?.id === id ? remembered : tab.route;
}
